import "../styles/SupervisorStatisticsDashboard.css";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/shared/LoadingSystem";
import SupervisorStatisticsSkeleton from "../components/SupervisorStatisticsSkeleton";
// Reemplazando axios por apiClient
import apiClient from "@shared/config/axiosConfig";
import { useDemo } from "@app/providers/DemoContext";
import ApiPlantas from "@shared/api/plantasService";
import userService from "@shared/api/usersService";
import PerfilProfesionalService from "@shared/api/perfilProfesionalService";
import {
  isAuthenticated,
  getUserRole,
  getUserPlanta,
} from "../../../../utils/authStorage";
import ActividadDetalleModal from "../../../plan-anual/ui/components/modals/ActividadDetalleModal";
import ReporteDetalleModal from "../../../reports/ui/components/ReporteDetalleModal";
import { sileo } from "sileo";
import useOnlineStatus from "@shared/hooks/useOnlineStatus";
import offlineDashboardCache from "../../../../services/offlineDashboardCache";
import OfflineBanner from "@shared/ui/offline-banner/OfflineBanner";
import useSocketEvent from "@shared/hooks/useSocketEvent";
import { joinPlanta, leavePlanta } from "@shared/config/socketClient";
const WeeklyReportsChart = lazy(
  () => import("../components/WeeklyReportsChart"),
);

export const getDashboardWeekNumber = (date = new Date()) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - startOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
};

export const getReportAxisScale = (values = []) => {
  const highest = Math.max(0, ...values.map(Number).filter(Number.isFinite));
  if (highest === 0) return { max: 1, stepSize: 1 };
  const stepSize = Math.max(1, Math.ceil(highest / 6));
  return {
    max: Math.ceil(highest / stepSize) * stepSize + stepSize,
    stepSize,
  };
};

/* Hallmark · pre-emit critique: P5 H5 E4 S5 R5 V4 */
const weeklyActivitiesRequests = new Map();

export const getWeeklyActivities = (idplanta, anio, semana) => {
  const key = `${idplanta}:${anio}:${semana}`;
  if (!weeklyActivitiesRequests.has(key)) {
    weeklyActivitiesRequests.set(
      key,
      apiClient
        .get(`/plantas/${idplanta}/actividades/semana/${semana}`, {
          params: { anio },
        })
        .finally(() => weeklyActivitiesRequests.delete(key)),
    );
  }
  return weeklyActivitiesRequests.get(key);
};

const SupervisorStatisticsDashboard = () => {
  // Combined: UI state
  const [uiState, setUiState] = useState({
    loading: true,
    isCel: false,
    imageLoading: false,
    isDarkTheme: false,
  });
  const { loading, isCel, imageLoading, isDarkTheme } = uiState;

  // Combined: dashboard data
  const [dashData, setDashData] = useState({
    reportStatistics: [],
    reportesPendientes: [],
    inspectoresPlanta: [],
    actividadesSemana: [],
    actividadesRealizadas: 0,
    actividadesPendientes: 0,
  });
  const {
    reportStatistics,
    reportesPendientes,
    inspectoresPlanta,
    actividadesSemana,
    actividadesRealizadas,
    actividadesPendientes,
  } = dashData;

  // Combined: planta info
  const [plantaInfo, setPlantaInfo] = useState({ name: "", image: null });
  const { name: plantaName, image: plantaImage } = plantaInfo;

  const [currentWeek, setCurrentWeek] = useState(() =>
    getDashboardWeekNumber(),
  );

  // Combined: modals
  const [modals, setModals] = useState({
    actividadOpen: false,
    actividadSeleccionada: null,
    reporteSeleccionado: null,
    reporteVisible: false,
  });
  const {
    actividadOpen: modalActividadOpen,
    actividadSeleccionada,
    reporteSeleccionado,
    reporteVisible: modalReporteVisible,
  } = modals;

  // Combined: compat (unused states kept for compatibility)
  // eslint-disable-next-line no-unused-vars
  const [compatState, setCompatState] = useState({
    reportCount: 0,
    colaboradores: [],
    reportesCompletados: { completados: 0, requeridos: 0 },
  });
  // eslint-disable-next-line no-unused-vars
  const { reportCount, colaboradores, reportesCompletados } = compatState;

  // Hooks
  const { isDemoMode, getDemoStatistics } = useDemo();
  const navigate = useNavigate();
  const { isOnline, isOffline } = useOnlineStatus();

  // Estado offline
  const [offlineState, setOfflineState] = useState({
    isOfflineMode: false,
    offlineSyncInfo: null,
  });
  const { isOfflineMode, offlineSyncInfo } = offlineState;

  // Referencia para el contenedor de reportes pendientes
  const pendingReportsRef = useRef(null);

  // Referencias para prevenir peticiones duplicadas
  const requestsInProgress = useRef({
    fetchReportes: false,
    fetchReportesCompletados: false,
    getPlantaById: false,
  });

  // Controlar que el efecto principal solo se ejecute una vez
  const dataFetched = useRef(false);

  const handleResize = useCallback(() => {
    setUiState((p) => ({ ...p, isCel: window.innerWidth < 768 }));
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  // Detectar tema claro/oscuro para ajustar colores del gráfico
  useEffect(() => {
    const root = document.documentElement;

    const updateTheme = () => {
      setUiState((p) => ({
        ...p,
        isDarkTheme: root.classList.contains("dark-theme"),
      }));
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    return () => {
      observer.disconnect();
    };
  }, []);

  // useEffect para detectar si hay scroll en reportes pendientes
  useEffect(() => {
    const checkScrollable = () => {
      if (pendingReportsRef.current) {
        const { scrollHeight, clientHeight } = pendingReportsRef.current;
        const hasScroll = scrollHeight > clientHeight;

        if (hasScroll) {
          pendingReportsRef.current.classList.add("has-scroll");
        } else {
          pendingReportsRef.current.classList.remove("has-scroll");
        }
      }
    };

    // Verificar cuando cambian los reportes pendientes
    checkScrollable();

    // Tambi�n verificar en resize
    window.addEventListener("resize", checkScrollable);

    return () => {
      window.removeEventListener("resize", checkScrollable);
    };
  }, [reportesPendientes]);

  // Funci�n para calcular la semana actual (memoizada para evitar c�lculos repetidos)
  const getCurrentWeekNumber = useCallback((date = new Date()) => {
    return getDashboardWeekNumber(date);
  }, []);

  const fetchReportes = useCallback(
    async (plantaId) => {
      // Si ya hay una petici�n en curso, no realizar otra
      if (requestsInProgress.current.fetchReportes) return;

      requestsInProgress.current.fetchReportes = true;

      try {
        // En modo demo, usar estad�sticas ficticias
        if (isDemoMode) {
          const demoStats = getDemoStatistics();
          if (demoStats && demoStats.reportesPorSemana) {
            setDashData((p) => ({
              ...p,
              reportStatistics: demoStats.reportesPorSemana,
            }));
            // setReportCount y setColaboradores ya no son necesarios en el nuevo layout
            // La información ahora se obtiene desde fetchActividadesPlanta

            const currentWeekNumber = getCurrentWeekNumber();
            setCurrentWeek(currentWeekNumber);

            console.log("📊 Usando estad�sticas de demo");
            return;
          }
        }

        const response = await apiClient.get(
          `/reportes/estadisticas-semanales/${plantaId}`,
        );

        // Solo actualizar estados si hay datos válidos
        if (response.data && Array.isArray(response.data)) {
          setDashData((p) => ({ ...p, reportStatistics: response.data }));
        }
      } catch (error) {
        console.error("Error fetchReportes:", error);
      } finally {
        requestsInProgress.current.fetchReportes = false;
      }
    },
    [getCurrentWeekNumber, isDemoMode, getDemoStatistics],
  );

  const fetchReportesCompletados = useCallback(async (idPlanta, date) => {
    // Si ya hay una petici�n en curso, no realizar otra
    if (requestsInProgress.current.fetchReportesCompletados) return;

    requestsInProgress.current.fetchReportesCompletados = true;

    try {
      // Usar API real tanto en modo demo como normal
      const body = { idPlanta, date };

      // Usar apiClient en lugar de axios con URL hardcodeada
      const response = await apiClient.post("/reportes/completados", body);

      // Solo procesamos la respuesta si hay datos
      if (response.data && Array.isArray(response.data)) {
        // Calcular completados y requeridos como lo hac�as originalmente
        const completados = response.data.reduce(
          (acc, reporte) => acc + reporte.completados,
          0,
        );
        const requeridos = response.data.reduce(
          (acc, reporte) => acc + reporte.requeridos,
          0,
        );

        // Limitar los completados para que no excedan los requeridos
        const completadosLimitados = Math.min(completados, requeridos);

        // Actualizar el estado con los valores adecuados
        setCompatState((p) => ({
          ...p,
          reportesCompletados: {
            completados: completadosLimitados,
            requeridos: requeridos,
          },
        }));

        const pendientes = response.data.filter(
          (reporte) => reporte.completados === 0,
        );

        setDashData((p) => ({ ...p, reportesPendientes: pendientes }));
      }
    } catch (error) {
      console.error("Error fetchReportesCompletados:", error);
    } finally {
      requestsInProgress.current.fetchReportesCompletados = false;
      setUiState((p) => ({ ...p, loading: false }));
    }
  }, []);

  // Funci�n para obtener la URL de la imagen de la planta (usando la misma l�gica que Plantas.jsx)
  const fetchImageUrl = useCallback(async (imagePath) => {
    try {
      const [folder, file] = imagePath.split("/");

      const response = await apiClient.get(`/files/${file}?folder=${folder}`);
      return response.data.url;
    } catch (error) {
      console.error("Error fetching image URL:", error);
      return null;
    }
  }, []);

  // Función para obtener inspectores (solo los que tienen perfil profesional creado)
  const fetchInspectoresPlanta = useCallback(async (idplanta) => {
    try {
      console.log("🔵 Fetching inspectores con perfil profesional");

      // Obtener todos los usuarios e inspectores activos
      const [usersResponse, perfilesResponse] = await Promise.all([
        userService.getAllUsers(),
        PerfilProfesionalService.obtenerPerfiles(),
      ]);

      const todosUsuarios = usersResponse.data || usersResponse || [];
      const perfiles = perfilesResponse.data || perfilesResponse || [];

      // Crear mapa de perfiles por idusuario
      const perfilesPorUsuario = new Map();
      perfiles.forEach((perfil) => {
        perfilesPorUsuario.set(perfil.idusuario, perfil);
      });

      // Filtrar solo inspectores activos (idrol = 2) que tengan perfil profesional
      const inspectores = todosUsuarios.filter(
        (user) =>
          user.idrol === 2 &&
          user.estado === true &&
          perfilesPorUsuario.has(user.idusuario),
      );

      console.log("✅ Inspectores con perfil encontrados:", inspectores.length);
      setDashData((p) => ({ ...p, inspectoresPlanta: inspectores }));
    } catch (error) {
      console.error("❌ Error al obtener inspectores:", error);
      setDashData((p) => ({ ...p, inspectoresPlanta: [] }));
    }
  }, []);

  // Funci�n para navegar al perfil de un inspector
  const handleInspectorClick = useCallback(
    (inspector) => {
      console.log("🔵 Navegando al perfil del inspector:", inspector);
      // Guardar el inspector seleccionado en sessionStorage para que CertificacionesSupervisor lo muestre
      sessionStorage.setItem(
        "colaboradorSeleccionado",
        JSON.stringify(inspector),
      );
      // Navegar a la página de certificaciones
      navigate("/supervisor/certificaciones");
    },
    [navigate],
  );

  // Función para abrir el modal con los detalles de una actividad
  const handleActividadClick = useCallback((actividad) => {
    console.log("📋 Mostrando detalles de actividad:", actividad);

    // Transformar la actividad al formato esperado por el modal
    const actividadFormateada = {
      ...actividad,
      titulo: actividad.plantilla?.titulo || "Actividad",
      descripcion: actividad.plantilla?.descripcion || "Sin descripción",
      tipoMantenimiento: actividad.tipoMantenimiento || "Sin tipo",
      componente:
        actividad.subcomponentes?.[0]?.componente?.descripcion ||
        "Sin componente",
      subcomponente:
        actividad.subcomponentes?.[0]?.descripcion || "Sin subcomponente",
      color:
        actividad.estado === "COMPLETADA"
          ? "#28a745"
          : actividad.estado === "PENDIENTE"
            ? "#ffc107"
            : "#dc3545",
    };

    setModals((p) => ({ ...p, actividadSeleccionada: actividadFormateada }));
    setModals((p) => ({ ...p, actividadOpen: true }));
  }, []);

  // Función para cerrar el modal de actividad
  const handleCloseActividadModal = useCallback(() => {
    setModals((p) => ({ ...p, actividadOpen: false }));
    setModals((p) => ({ ...p, actividadSeleccionada: null }));
  }, []);

  // Función para ver el reporte asociado a una actividad
  const handleVerReporte = useCallback(async (idreporte) => {
    if (!idreporte) {
      sileo.error({ title: "No hay reporte asociado a esta actividad" });
      return;
    }

    // Cerrar modal de actividad antes de abrir el de reporte
    setModals((p) => ({ ...p, actividadOpen: false }));
    setModals((p) => ({ ...p, actividadSeleccionada: null }));

    try {
      const response = await apiClient.get(`/reportes/${idreporte}`);

      if (response.data && response.data.reporte) {
        setModals((p) => ({
          ...p,
          reporteSeleccionado: response.data.reporte,
        }));
        setModals((p) => ({ ...p, reporteVisible: true }));
      } else {
        sileo.error({ title: "No se encontró el reporte" });
      }
    } catch (error) {
      console.error("Error al cargar reporte:", error);
      sileo.error({ title: "Error al cargar el reporte" });
    }
  }, []);

  // Función para cerrar el modal de reporte
  const handleCloseReporteModal = useCallback(() => {
    setModals((p) => ({ ...p, reporteVisible: false }));
    setModals((p) => ({ ...p, reporteSeleccionado: null }));
  }, []);

  // Funci�n para obtener actividades de la planta
  const fetchActividadesPlanta = useCallback(
    async (idplanta) => {
      try {
        const anioActual = new Date().getFullYear();
        const fechaHoy = new Date();

        console.log("?? Obteniendo actividades de la planta", idplanta);

        // Obtener todas las actividades del a�o
        const semanaActual = getCurrentWeekNumber(fechaHoy);
        const response = await getWeeklyActivities(
          idplanta,
          anioActual,
          semanaActual,
        );
        const actividadesDeLaSemana = response.data || [];

        // Filtrar actividades de la semana actual
        // Calcular contadores
        const realizadas = actividadesDeLaSemana.filter(
          (a) => a.estado === "COMPLETADA",
        ).length;
        const pendientes = actividadesDeLaSemana.filter(
          (a) => a.estado === "PENDIENTE",
        ).length;

        console.log("? Actividades cargadas:", {
          semana: actividadesDeLaSemana.length,
          realizadas,
          pendientes,
        });

        setDashData((p) => ({
          ...p,
          actividadesSemana: actividadesDeLaSemana,
          actividadesRealizadas: realizadas,
          actividadesPendientes: pendientes,
        }));
      } catch (error) {
        console.error("? Error al obtener actividades:", error);
        setDashData((p) => ({
          ...p,
          actividadesSemana: [],
          actividadesRealizadas: 0,
          actividadesPendientes: 0,
        }));
      }
    },
    [getCurrentWeekNumber],
  );

  // Funci�n para navegar al perfil de un inspector
  useEffect(() => {
    const fetchData = async () => {
      // Si ya se cargaron los datos, no volver a cargarlos
      if (dataFetched.current) return;

      setUiState((p) => ({ ...p, loading: true }));

      try {
        // En modo demo, cargar datos ficticios directamente
        if (isDemoMode) {
          console.log("🎬 Cargando datos de demo para StatisticPanel");

          const demoStats = getDemoStatistics();
          if (demoStats) {
            // Configurar datos de demo
            setPlantaInfo((p) => ({
              ...p,
              name: "Darpha Fire Solutions",
              image: "/darpha_logo_digital.png",
            }));
            setCurrentWeek(demoStats.semanaActual || getCurrentWeekNumber());
            setDashData((p) => ({
              ...p,
              reportStatistics: demoStats.reportesPorSemana || [],
            }));
            setCompatState((p) => ({
              ...p,
              reportCount:
                demoStats.reportesPorSemana?.[
                  demoStats.reportesPorSemana.length - 1
                ]?.reportes || 8,
            }));
            setCompatState((p) => ({
              ...p,
              colaboradores: demoStats.colaboradores || [
                "Juan P�rez",
                "Mar�a Gonz�lez",
                "Carlos Rodr�guez",
              ],
            }));

            console.log("📊 Datos de demo cargados exitosamente");

            // En modo demo, usar la planta demo ID (44) para obtener reportes reales de la API
            const currentDate = new Date().toISOString().split("T")[0];
            await fetchReportesCompletados(44, currentDate);

            // Cargar inspectores de la planta demo
            await fetchInspectoresPlanta(44);
          } else {
            console.warn(
              "⚠️ No se encontraron datos de demo, usando valores por defecto",
            );
            // Datos por defecto si no hay estad�sticas de demo
            setPlantaInfo((p) => ({
              ...p,
              name: "Darpha Fire Solutions (Demo)",
              image: "/darpha_logo_digital.png",
            }));
            setCurrentWeek(getCurrentWeekNumber());
            setCompatState((p) => ({ ...p, reportCount: 8 }));
            setCompatState((p) => ({
              ...p,
              colaboradores: [
                "Juan P�rez",
                "Mar�a Gonz�lez",
                "Carlos Rodr�guez",
              ],
            }));

            // Tambi�n usar API real para datos por defecto
            const currentDate = new Date().toISOString().split("T")[0];
            await fetchReportesCompletados(44, currentDate);

            // Cargar inspectores de la planta demo
            await fetchInspectoresPlanta(44);
          }

          dataFetched.current = true;
          setUiState((p) => ({ ...p, loading: false }));
          return;
        }

        // L�gica normal para modo no-demo
        if (!isAuthenticated()) {
          setUiState((p) => ({ ...p, loading: false }));
          return;
        }

        const currentRole = getUserRole();

        // Determinar la planta ID de manera adecuada
        const plantaId =
          currentRole === 5
            ? sessionStorage.getItem("PlantaDevelop")
            : getUserPlanta();

        if (!plantaId) {
          setUiState((p) => ({ ...p, loading: false }));
          return;
        }

        // Marcar que el proceso de carga ha comenzado
        dataFetched.current = true;

        // Estos datos solo necesitan el id de planta. Arrancarlos antes evita
        // bloquear las estadisticas por la ficha o la imagen de la planta.
        const currentDate = new Date().toISOString().split("T")[0];
        const dashboardRequest = Promise.all([
          fetchReportes(plantaId),
          fetchReportesCompletados(plantaId, currentDate),
          fetchInspectoresPlanta(plantaId),
          fetchActividadesPlanta(plantaId),
        ]);

        // Evitar solicitudes dobles usando la referencia
        if (!requestsInProgress.current.getPlantaById) {
          requestsInProgress.current.getPlantaById = true;

          // Obtener informaci�n de la planta
          const plantaData = await ApiPlantas.getPlantaById(plantaId);
          requestsInProgress.current.getPlantaById = false;

          if (plantaData && plantaData.nombreplanta) {
            setPlantaInfo((p) => ({ ...p, name: plantaData.nombreplanta }));

            // Cargar imagen de la planta usando la misma l�gica que Plantas.jsx
            if (plantaData.image) {
              setUiState((p) => ({ ...p, imageLoading: true }));
              try {
                const imageUrl = await fetchImageUrl(plantaData.image);
                setPlantaInfo((p) => ({ ...p, image: imageUrl }));
              } catch (error) {
                console.error("Error al cargar imagen de la planta:", error);
              } finally {
                setUiState((p) => ({ ...p, imageLoading: false }));
              }
            }

          } else {
            setUiState((p) => ({ ...p, loading: false }));
          }
        }

        await dashboardRequest;

        // Configurar la semana actual
        setCurrentWeek(getCurrentWeekNumber());
        setOfflineState({ isOfflineMode: false, offlineSyncInfo: null });
      } catch (error) {
        console.error("Error en fetchData:", error);
        dataFetched.current = false;

        // Intentar cargar desde caché offline
        try {
          const currentRole = getUserRole();
          const plantaId =
            currentRole === 5
              ? sessionStorage.getItem("PlantaDevelop")
              : getUserPlanta();

          if (plantaId) {
            const cached = await offlineDashboardCache.getDashboard(plantaId);
            const syncInfo = await offlineDashboardCache.getSyncInfo(plantaId);

            if (cached) {
              setPlantaInfo((p) => ({
                ...p,
                name: cached.plantaName || "",
                image: cached.plantaImage || null,
              }));
              setCurrentWeek(cached.currentWeek || getCurrentWeekNumber());
              setDashData((p) => ({
                ...p,
                reportStatistics: cached.reportStatistics || [],
                reportesPendientes: cached.reportesPendientes || [],
                inspectoresPlanta: cached.inspectoresPlanta || [],
                actividadesSemana: cached.actividadesSemana || [],
                actividadesRealizadas: cached.actividadesRealizadas || 0,
                actividadesPendientes: cached.actividadesPendientes || 0,
              }));
              setOfflineState({
                isOfflineMode: true,
                offlineSyncInfo: syncInfo,
              });
              console.log("📱 Dashboard cargado desde caché offline");
              sileo.info({
                title: "Modo sin conexión",
                description: "Mostrando datos guardados localmente",
              });
            } else {
              sileo.error({
                title: "Sin conexión",
                description: "No hay datos guardados para ver sin conexión.",
              });
            }
          }
        } catch (cacheError) {
          console.error("Error cargando caché offline:", cacheError);
        }

        setUiState((p) => ({ ...p, loading: false }));
      }
    };

    fetchData();

    // Cleanup function para cancelar peticiones pendientes en caso de desmontaje
    return () => {
      dataFetched.current = false;
    };
  }, [
    fetchReportes,
    fetchReportesCompletados,
    getCurrentWeekNumber,
    fetchImageUrl,
    fetchInspectoresPlanta,
    fetchActividadesPlanta,
    isDemoMode,
    getDemoStatistics,
  ]); // Dependencias de las funciones memoizadas

  // Guardar snapshot en caché cuando los datos se cargan con éxito (no offline, no demo)
  useEffect(() => {
    if (isOfflineMode || isDemoMode || loading) return;
    if (
      !plantaName ||
      (actividadesSemana.length === 0 && inspectoresPlanta.length === 0)
    )
      return;

    const currentRole = getUserRole();
    const plantaId =
      currentRole === 5
        ? sessionStorage.getItem("PlantaDevelop")
        : getUserPlanta();

    if (!plantaId) return;

    offlineDashboardCache.saveDashboard(plantaId, {
      plantaName,
      plantaImage,
      currentWeek,
      reportStatistics,
      reportesPendientes,
      inspectoresPlanta,
      actividadesSemana,
      actividadesRealizadas,
      actividadesPendientes,
    });
  }, [
    loading,
    isOfflineMode,
    isDemoMode,
    plantaName,
    plantaImage,
    currentWeek,
    reportStatistics,
    reportesPendientes,
    inspectoresPlanta,
    actividadesSemana,
    actividadesRealizadas,
    actividadesPendientes,
  ]);

  // Recargar datos cuando la conexión se restaure
  useEffect(() => {
    if (isOnline && isOfflineMode) {
      console.log("🔄 Conexión restaurada, recargando dashboard...");
      sileo.success({
        title: "Conexión restaurada",
        description: "Actualizando datos desde el servidor...",
      });
      dataFetched.current = false;
      setOfflineState({ isOfflineMode: false, offlineSyncInfo: null });
      // El efecto principal se re-ejecutará al cambiar dataFetched
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // 🔌 Socket.io: Determinar planta del supervisor
  const socketPlantaId = useMemo(() => {
    const currentRole = getUserRole();
    if (currentRole === 5) return sessionStorage.getItem("PlantaDevelop");
    return getUserPlanta();
  }, []);

  // 🔌 Socket.io: Unirse a sala de planta
  useEffect(() => {
    if (!socketPlantaId) return;
    joinPlanta(socketPlantaId);
    return () => leavePlanta(socketPlantaId);
  }, [socketPlantaId]);

  // 🔌 Socket.io: Refrescar dashboard al crear reporte o actualizar actividad
  const handleSocketRefresh = useCallback(() => {
    if (!socketPlantaId) return;
    console.log("🔌 Evento socket recibido, refrescando dashboard...");
    const currentDate = new Date().toISOString().split("T")[0];
    fetchReportes(socketPlantaId);
    fetchReportesCompletados(socketPlantaId, currentDate);
    fetchActividadesPlanta(socketPlantaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    socketPlantaId,
    fetchReportes,
    fetchReportesCompletados,
    fetchActividadesPlanta,
  ]);

  useSocketEvent("report-created", handleSocketRefresh, !!socketPlantaId);
  useSocketEvent("activity-updated", handleSocketRefresh, !!socketPlantaId);
  useSocketEvent("report-signed", handleSocketRefresh, !!socketPlantaId);

  // Memoizaci�n de datos del gr�fico para evitar c�lculos innecesarios
  const chartData = useMemo(() => {
    const weekLabels = [];
    const reportCounts = [];

    // En modo demo, usar los datos de reportesPorSemana si est�n disponibles
    if (
      reportStatistics?.length > 0 &&
      reportStatistics.every((item) => "semana" in item && "reportes" in item)
    ) {
      reportStatistics.forEach((weekData) => {
        weekLabels.push(`Semana ${weekData.semana}`);
        reportCounts.push(weekData.reportes);
      });
    } else if (!isDemoMode && reportStatistics && reportStatistics.length > 0) {
      // Lógica normal para modo no-demo
      const currentDate = new Date();
      const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
      const currentWeek = Math.ceil(
        ((currentDate - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7,
      );

      // Recorre desde la semana 0 hasta la actual (siempre empieza en 0)
      const startWeek = Math.max(0, currentWeek - 4);
      const endWeek = currentWeek;

      for (let week = startWeek; week <= endWeek; week++) {
        weekLabels.push(`Semana ${week}`);
        const count = reportStatistics.filter((report) => {
          const reportDate = new Date(report.fecha);
          const startOfYearReport = new Date(reportDate.getFullYear(), 0, 1);
          const reportWeek = Math.ceil(
            ((reportDate - startOfYearReport) / 86400000 +
              startOfYearReport.getDay() +
              1) /
              7,
          );
          return reportWeek === week;
        }).length;
        reportCounts.push(count);
      }
    } else {
      // Datos por defecto si no hay nada
      const currentDate = new Date();
      const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
      const currentWeek = Math.ceil(
        ((currentDate - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7,
      );

      const startWeek = Math.max(0, currentWeek - 4);
      for (let i = startWeek; i <= currentWeek; i++) {
        weekLabels.push(`Semana ${i}`);
        reportCounts.push(0);
      }
    }

    return {
      labels: weekLabels,
      datasets: [
        {
          label: "Cantidad de Reportes",
          data: reportCounts,
          borderColor: "#3498db",
          backgroundColor: "rgba(52, 152, 219, 0.2)",
          fill: true,
          tension: 0.3,
          borderWidth: 2,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: "#3498db",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [reportStatistics, isDemoMode]);

  // Configuraci�n mejorada del gr�fico
  const chartOptions = useMemo(() => {
    const axisTextColor = isDarkTheme
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(0, 0, 0, 0.8)";
    const axisGridColor = isDarkTheme
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.06)";
    const reportValues = chartData.datasets[0]?.data || [];
    const axisScale = getReportAxisScale(reportValues);
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: 30,
          left: 5,
          right: 5,
          top: 5,
        },
      },
      plugins: {
        reportValueLabels: {
          color: axisTextColor,
          fontSize: isCel ? 11 : 13,
        },
        legend: {
          position: "top",
          align: "end",
          labels: {
            usePointStyle: true,
            boxWidth: 6,
            boxHeight: 6,
            padding: 15,
            font: {
              size: isCel ? 10 : 12,
              family: "'Segoe UI', 'Roboto', sans-serif",
            },
            color: axisTextColor,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(44, 62, 80, 0.85)",
          titleFont: {
            size: isCel ? 11 : 13,
            weight: "bold",
          },
          bodyFont: {
            size: isCel ? 10 : 12,
          },
          padding: 10,
          cornerRadius: 6,
          displayColors: false,
          callbacks: {
            title: function (tooltipItems) {
              return tooltipItems[0].label;
            },
            label: function (context) {
              return `Reportes: ${context.parsed.y}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: axisScale.max,
          title: {
            display: true,
            text: "Número de reportes",
            color: axisTextColor,
            font: {
              size: isCel ? 11 : 13,
              family: "'Segoe UI', 'Roboto', sans-serif",
              weight: "bold",
            },
          },
          grid: {
            color: axisGridColor,
            drawBorder: false,
          },
          ticks: {
            stepSize: axisScale.stepSize,
            precision: 0,
            callback: (value) => (Number.isInteger(value) ? value : null),
            font: {
              size: isCel ? 10 : 12,
              family: "'Segoe UI', 'Roboto', sans-serif",
            },
            padding: 8,
            color: axisTextColor,
          },
          border: {
            dash: [4, 4],
          },
        },
        x: {
          grid: {
            display: true,
            color: axisGridColor,
            drawBorder: false,
          },
          title: {
            display: true,
            text: "Semana de reportes",
            color: axisTextColor,
            font: {
              size: isCel ? 11 : 13,
              family: "'Segoe UI', 'Roboto', sans-serif",
              weight: "bold",
            },
          },
          ticks: {
            font: {
              size: isCel ? 10 : 13,
              family: "'Segoe UI', 'Roboto', sans-serif",
              weight: "500",
            },
            padding: 10,
            color: axisTextColor,
            autoSkip: isCel,
            maxRotation: 0,
            minRotation: 0,
          },
        },
      },
      elements: {
        point: {
          radius: isCel ? 3 : 4,
          hoverRadius: isCel ? 5 : 7,
        },
        line: {
          tension: 0.3,
        },
      },
      animation: {
        duration: prefersReducedMotion ? 0 : 450,
        easing: "easeOutQuart",
      },
    };
  }, [chartData, isCel, isDarkTheme]);

  return (
    <div className="layout-container-statistic-panel">
      {(isOfflineMode || isOffline) && (
        <OfflineBanner syncInfo={offlineSyncInfo} />
      )}
      <div className="control-statistic-panel">
        <header className="control-statistic-title">
          {/* Header: logo + nombre planta a la izquierda */}
          <div className="plant-header-container">
            <div className="plant-info">
              <div className="plant-logo-container">
                {imageLoading ? (
                  <div className="plant-logo-skeleton">
                    <Skeleton
                      variant="rectangular"
                      width="28px"
                      height="28px"
                      borderRadius="6px"
                    />
                  </div>
                ) : plantaImage ? (
                  <img
                    src={plantaImage}
                    alt={`Logo de ${plantaName}`}
                    className="plant-logo"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="plant-logo-fallback"
                  style={{ display: plantaImage ? "none" : "flex" }}
                >
                  <i className="fas fa-fire" aria-hidden="true"></i>
                </div>
              </div>
              <div className="dashboard-heading-copy">
                <h1 className="plant-name">
                  {plantaName || "Panel de supervisión"}
                </h1>
                <p className="plant-title-statistic-panel">
                  Panel de supervisión
                  <span className="header-context-separator" aria-hidden="true">
                    ·
                  </span>
                  Semana {currentWeek}
                </p>
              </div>
            </div>
          </div>
          {/* Título descriptivo a la derecha */}
        </header>
        <div className="dashboard-cards-container-statistic-panel">
          {loading ? (
            <SupervisorStatisticsSkeleton />
          ) : (
            <>
              {/* Grid Layout - 3 columnas x 5 filas */}
              <div className="dashboard-grid-statistic-panel">
                {/* Div 1: Semana Actual */}
                <div className="card-statistic-panel grid-item-1 kpi-card">
                  <div className="kpi-card-content">
                    <div className="kpi-label">
                      <span className="kpi-dot kpi-dot-blue"></span>
                      <span>Semana Actual</span>
                    </div>
                    <p className="kpi-number">{currentWeek}</p>
                  </div>
                </div>

                {/* Div 2: Actividades Realizadas en la Semana */}
                <div className="card-statistic-panel grid-item-2 kpi-card">
                  <div className="kpi-card-content">
                    <div className="kpi-label">
                      <span className="kpi-dot kpi-dot-green"></span>
                      <span>Actividades Realizadas</span>
                    </div>
                    <p className="kpi-number">{actividadesRealizadas}</p>
                  </div>
                </div>

                {/* Div 3: Actividades Pendientes en la Semana */}
                <div className="card-statistic-panel grid-item-3 kpi-card">
                  <div className="kpi-card-content">
                    <div className="kpi-label">
                      <span className="kpi-dot kpi-dot-red"></span>
                      <span>Actividades Pendientes</span>
                    </div>
                    <p className="kpi-number">{actividadesPendientes}</p>
                  </div>
                </div>

                {/* Div 5: Gr�fica */}
                <div className="card-statistic-panel grid-item-5">
                  <div className="card-content-chart">
                    <i className="titulos-cards-statistic-panel">
                      Reportes Semanales
                    </i>
                    <div className="chart-container">
                      <Suspense
                        fallback={
                          <div
                            style={{
                              height: "200px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            Cargando gráfica...
                          </div>
                        }
                      >
                        <WeeklyReportsChart
                          data={chartData.labels.map((semana, index) => ({
                            semana,
                            reportes: chartData.datasets[0].data[index],
                          }))}
                          axisScale={getReportAxisScale(
                            chartData.datasets[0].data,
                          )}
                          isMobile={isCel}
                        />
                      </Suspense>
                    </div>
                  </div>
                </div>

                {/* Div 6: Lista de Inspectores/Colaboradores */}
                <div className="card-statistic-panel grid-item-6">
                  <div className="card-content-list">
                    <div className="inspectores-heading">
                      <h2 className="titulos-cards-statistic-panel">
                        Inspectores
                      </h2>
                      <span className="inspectores-count">
                        {inspectoresPlanta.length}{" "}
                        {inspectoresPlanta.length === 1
                          ? "asignado"
                          : "asignados"}
                      </span>
                    </div>
                    {inspectoresPlanta.length > 0 ? (
                      <div className="inspectores-lista">
                        {inspectoresPlanta.map((inspector) => (
                          <div
                            role="button"
                            tabIndex={0}
                            key={inspector.idusuario}
                            className="inspector-card"
                            onClick={() => handleInspectorClick(inspector)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                (() => handleInspectorClick(inspector))(e);
                              }
                            }}
                            title="Ver perfil profesional"
                          >
                            <div className="inspector-avatar" aria-hidden="true">
                              {inspector.nombreusuario
                                ?.trim()
                                .split(/\s+/)
                                .slice(0, 2)
                                .map((nombre) => nombre[0])
                                .join("")
                                .toUpperCase() || "I"}
                            </div>
                            <div className="inspector-info">
                              <span className="inspector-nombre">
                                {inspector.nombreusuario}
                              </span>
                              <span className="inspector-email">
                                {inspector.correoelectronico}
                              </span>
                            </div>
                            <span className="inspector-action">
                              Ver perfil
                              <i
                                className="fas fa-arrow-right"
                                aria-hidden="true"
                              ></i>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-inspectores">
                        No hay inspectores disponibles
                      </p>
                    )}
                  </div>
                </div>

                {/* Div 7: Lista de Actividades de la Semana */}
                <div className="card-statistic-panel grid-item-7">
                  <div className="card-content-list">
                    <h2 className="titulos-cards-statistic-panel">
                      Actividades de la Semana
                    </h2>
                    <div className="actividades-lista">
                      {actividadesSemana.length > 0 ? (
                        actividadesSemana.map((actividad) => (
                          <div
                            role="button"
                            tabIndex={0}
                            key={actividad.idactividadprogramada}
                            className="actividad-item-semana"
                            onClick={() => handleActividadClick(actividad)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                (() => handleActividadClick(actividad))(e);
                              }
                            }}
                            title="Ver detalles de la actividad"
                          >
                            <div className="actividad-contenido">
                              <span className="actividad-titulo">
                                {actividad.plantilla?.titulo || "Actividad"}
                              </span>
                              <div className="actividad-detalles">
                                <span className="actividad-fecha">
                                  <i
                                    className="fas fa-calendar"
                                    aria-hidden="true"
                                  ></i>
                                  {new Date(
                                    actividad.fechainicio,
                                  ).toLocaleDateString("es-MX")}
                                </span>
                                {actividad.inspectorAsignado && (
                                  <span className="actividad-inspector">
                                    <i
                                      className="fas fa-user"
                                      aria-hidden="true"
                                    ></i>
                                    {actividad.inspectorAsignado.nombreusuario}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span
                              className={`actividad-badge estado-${actividad.estado.toLowerCase()}`}
                            >
                              {actividad.estado}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="no-actividades">
                          No hay actividades programadas esta semana
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de detalle de actividad */}
      {modalActividadOpen && (
        <ActividadDetalleModal
          actividad={actividadSeleccionada}
          isOpen={modalActividadOpen}
          onClose={handleCloseActividadModal}
          onVerReporte={handleVerReporte}
        />
      )}

      {/* Modal de detalle de reporte */}
      {modalReporteVisible && (
        <ReporteDetalleModal
          report={reporteSeleccionado}
          isOpen={modalReporteVisible}
          onClose={handleCloseReporteModal}
          userRole={getUserRole()}
        />
      )}
    </div>
  );
};

export default SupervisorStatisticsDashboard;
//prueba de commit con comandos git
